import { useState, useEffect } from 'react'
import { Aircraft } from '../../types/intellijetnet'
import { UndoableMovement } from '../../types/database'
import { AircraftMovementService } from '../../services/aircraft-movement.service'
import { MovementAuditService, MovementAuditEntry } from '../../services/movement-audit.service'

interface MovementHistoryPanelProps {
  aircraft: Aircraft
  modelId: number
  onUpdate: () => void
  className?: string
}

export default function MovementHistoryPanel({
  aircraft,
  modelId,
  onUpdate,
  className = ''
}: MovementHistoryPanelProps) {
  const [undoableMovements, setUndoableMovements] = useState<UndoableMovement[]>([])
  const [movementHistory, setMovementHistory] = useState<MovementAuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [isUndoing, setIsUndoing] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    loadMovementData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft.aircraft_id, modelId])

  const loadMovementData = async () => {
    try {
      setLoading(true)
      
      // Load undoable movements for this model
      const undoable = await AircraftMovementService.getUndoableMovements(modelId)
      const aircraftUndoable = undoable.filter(m => m.aircraft_id === aircraft.aircraft_id)
      setUndoableMovements(aircraftUndoable)
      
      // Load movement history for this aircraft
      const history = await MovementAuditService.getAircraftMovementHistory(aircraft.aircraft_id)
      setMovementHistory(history)
      
    } catch (error) {
      // Error loading movement data
    } finally {
      setLoading(false)
    }
  }

  const handleUndoMovement = async (movementId: string) => {
    try {
      setIsUndoing(movementId)
      
      await AircraftMovementService.undoMovement(movementId, 'current_user') // TODO: Get from auth
      
      // Refresh data
      await loadMovementData()
      onUpdate()
      
      alert('Movement successfully undone')
      
    } catch (error) {
      alert('Failed to undo movement. Please try again.')
    } finally {
      setIsUndoing(null)
    }
  }

  const formatTimeRemaining = (hoursUntilExpiry: number): string => {
    if (hoursUntilExpiry <= 0) return 'Expired'
    
    const days = Math.floor(hoursUntilExpiry / 24)
    const hours = Math.floor(hoursUntilExpiry % 24)
    
    if (days > 0) {
      return `${days}d ${hours}h remaining`
    } else {
      return `${hours}h remaining`
    }
  }

  const getContainerDisplayName = (container: string): string => {
    switch (container) {
      case 'for_sale': return 'For Sale'
      case 'sold': return 'Sold'
      case 'removed_from_market': return 'Removed from Market'
      default: return container
    }
  }

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'move':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        )
      case 'undo':
        return (
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        )
      case 'confirm':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'expire':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
    }
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    )
  }

  if (undoableMovements.length === 0 && movementHistory.length === 0) {
    return null // No movement data to show
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Movement History</h3>
        {movementHistory.length > 0 && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showHistory ? 'Hide History' : 'Show Full History'}
          </button>
        )}
      </div>

      {/* Undoable Movements */}
      {undoableMovements.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Pending Movements (Can Undo)</h4>
          <div className="space-y-2">
            {undoableMovements.map((movement) => (
              <div key={movement.movement_id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Moved from {getContainerDisplayName(movement.from_container)} to {getContainerDisplayName(movement.to_container)}
                  </div>
                  <div className="text-xs text-gray-600">
                    {new Date(movement.moved_at).toLocaleString()} • {formatTimeRemaining(movement.hours_until_expiry)}
                  </div>
                  {movement.movement_reason && (
                    <div className="text-xs text-gray-500 mt-1">
                      Reason: {movement.movement_reason}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleUndoMovement(movement.movement_id)}
                  disabled={isUndoing === movement.movement_id || !movement.can_undo}
                  className="ml-3 px-3 py-1 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUndoing === movement.movement_id ? 'Undoing...' : 'Undo'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Movement History */}
      {showHistory && movementHistory.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Complete History</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {movementHistory.map((entry) => (
              <div key={entry.audit_id} className="flex items-start space-x-3 p-2 bg-gray-50 rounded-md">
                <div className="flex-shrink-0 mt-1">
                  {getActionIcon(entry.action_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">
                    <span className="font-medium capitalize">{entry.action_type}</span>: {getContainerDisplayName(entry.from_container)} → {getContainerDisplayName(entry.to_container)}
                  </div>
                  <div className="text-xs text-gray-600">
                    {new Date(entry.action_timestamp).toLocaleString()}
                    {entry.user_name && ` • by ${entry.user_name}`}
                    {entry.api_synchronized && ' • API Synchronized'}
                  </div>
                  {entry.reason && (
                    <div className="text-xs text-gray-500 mt-1">
                      {entry.reason}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
